import { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useProjects } from "../../../projects/projectContext";
import AppButton from "../../../ui/buttons/AppButton";
import Chip from "../../../ui/feedback/Chip";
import EmptyState from "../../../ui/feedback/EmptyState";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import Field from "../../../ui/forms/Field";
import { FORM_COMPACT_CONTROL_CLASS } from "../../../ui/styles/formStyles";
import { typesHint, typesText } from "../inputCards/typeWords";
import StepKindMark from "../graph/StepKindMark";
import type { WorkflowRole } from "../model/workflowDraft";
import { writePaletteDrag, type PaletteItem } from "./paletteDrag";
import { usePickerItems } from "./pickerItems";
import { pickerGroups, searchPicker, type PickerEntry, type PickerGroup, type PickerPort } from "./pickerEntries";

interface StepPickerProps {
  projectId: string;
  /** The edited Workflow's item ID: it cannot be its own sub-workflow. */
  selfItemId: string;
  onAdd: (item: PaletteItem, role: WorkflowRole) => void;
  onNewStep: () => void;
}

/**
 * Adding a step to the Workflow (AU-6, F36): a searchable list beside the graph, as node
 * editors such as n8n, Node-RED and Unreal's Blueprints offer it, grouped by where each
 * step comes from: this project, each library it pins, and the Workflows that can be
 * sub-workflows. Each step shows what it takes and gives, with types, and the search
 * finds a step by a type too. Drag one onto the main row or the inputs lane above it, or
 * use its buttons. The user's proposed versions are marked; they run once merged.
 */
export default function StepPicker({ projectId, selfItemId, onAdd, onNewStep }: StepPickerProps) {
  const { t } = useTranslation();
  const searchId = useId();
  const [query, setQuery] = useState("");
  const { state, reload } = usePickerItems(projectId);
  const groups = useMemo(() => (state.status === "loaded" ? pickerGroups(state.value, selfItemId) : []), [state, selfItemId]);
  const shown = useMemo(() => searchPicker(groups, query, t), [groups, query, t]);
  return (
    <aside className="app-card space-y-3 p-3" aria-label={t("workflows.edit.picker.title")}>
      <h3 className="app-title text-sm font-semibold">{t("workflows.edit.picker.title")}</h3>
      {state.status !== "loaded" ? (
        <LoadStatus state={state} loadingLabel={t("workflows.edit.picker.loading")} onRetry={reload} compact={true} />
      ) : (
        <>
          <Field id={searchId} labelHidden label={t("workflows.edit.picker.search")}>
            {(control) => (
              <input {...control} type="search" className={FORM_COMPACT_CONTROL_CLASS} placeholder={t("workflows.edit.picker.search")} value={query} onChange={(event) => { setQuery(event.target.value); }} />
            )}
          </Field>
          {shown.length === 0 ? (
            <EmptyState size="small">{groups.length === 0 ? t("workflows.edit.picker.empty") : t("workflows.edit.picker.noMatch")}</EmptyState>
          ) : (
            <div className="max-h-[32rem] space-y-3 overflow-y-auto">
              {shown.map((group) => <Group key={groupKey(group)} group={group} onAdd={onAdd} />)}
            </div>
          )}
        </>
      )}
      <AppButton variant="secondary" size="sm" fullWidth={true} onClick={onNewStep}>{t("workflows.edit.newStep")}</AppButton>
      <p className="app-help-text text-xs">{t("workflows.edit.paletteHint")}</p>
    </aside>
  );
}

function Group({ group, onAdd }: { group: PickerGroup; onAdd: StepPickerProps["onAdd"] }) {
  const { t } = useTranslation();
  const headingId = useId();
  const { projects } = useProjects();
  const source = group.source;
  const heading =
    source.kind === "library"
      ? t("workflows.edit.picker.library", { name: (projects.status === "loaded" ? projects.value.find((project) => project.projectId === source.projectId)?.name : undefined) ?? source.projectId })
      : t(`workflows.edit.picker.groups.${source.kind}`);
  return (
    <section className="space-y-1" aria-labelledby={headingId}>
      <h4 id={headingId} className="app-label text-xs">
        {heading} <span className="app-text-muted">({group.entries.length})</span>
      </h4>
      <ul className="space-y-1">
        {group.entries.map((entry) => <Entry key={`${entry.item.reference} ${entry.proposedIn?.changeSetId ?? "latest"}`} entry={entry} onAdd={onAdd} />)}
      </ul>
    </section>
  );
}

function Entry({ entry, onAdd }: { entry: PickerEntry; onAdd: StepPickerProps["onAdd"] }) {
  const { t } = useTranslation();
  const { item } = entry;
  return (
    <li
      className="app-surface-muted app-flow-palette-item space-y-0.5 rounded px-2 py-1 text-xs"
      draggable={true}
      title={entry.description === "" ? undefined : entry.description}
      onDragStart={(event) => {
        writePaletteDrag(event.dataTransfer, item);
      }}
    >
      <span className="flex flex-wrap items-center gap-1">
        <span className="min-w-0 flex-1 truncate font-semibold">{item.name}</span>
        {/* A step's kind as the cards and the legend show it (F61, F112); a sub-workflow, its count of steps. */}
        {entry.stepKind === null ? <Chip size="compact">{t("workflows.node.steps", { count: entry.stepCount ?? 0 })}</Chip> : <StepKindMark kind={entry.stepKind} />}
        {entry.proposedIn === null ? null : <Chip size="compact" title={entry.proposedIn.title}>{t("workflows.edit.proposed")}</Chip>}
      </span>
      {entry.stepKind === null ? null : (
        <dl className="app-text-muted grid grid-cols-[auto_1fr] gap-x-1.5">
          <dt>{t("workflows.edit.picker.takes")}</dt>
          <dd><Ports ports={entry.takes} /></dd>
          <dt>{t("workflows.edit.picker.gives")}</dt>
          <dd><Ports ports={entry.gives} /></dd>
        </dl>
      )}
      <span className="flex gap-1">
        <AppButton variant="ghost" size="xs" aria-label={t("workflows.edit.addMainNamed", { name: item.name })} onClick={() => { onAdd(item, "main"); }}>
          {t("workflows.edit.addMain")}
        </AppButton>
        <AppButton variant="ghost" size="xs" aria-label={t("workflows.edit.addInputNamed", { name: item.name })} onClick={() => { onAdd(item, "input"); }}>
          {t("workflows.edit.addInput")}
        </AppButton>
      </span>
    </li>
  );
}

/** "candidate claims · Text or Annotations, guideline · Item (optional)", as the graph's ports say it. */
function Ports({ ports }: { ports: readonly PickerPort[] }) {
  const { t } = useTranslation();
  if (ports.length === 0) {
    return <>{t("workflows.edit.picker.nothing")}</>;
  }
  return (
    <>
      {ports.map((port, index) => (
        <span key={port.name}>
          {index === 0 ? null : ", "}
          <span className="app-title">{port.name}</span> · <span className="app-flow-port-type" title={typesHint(port.types, t) ?? undefined}>{typesText(port.types, t)}</span>
          {port.need === "optional" ? ` (${t("workflows.node.port.needs.optional")})` : null}
        </span>
      ))}
    </>
  );
}

function groupKey(group: PickerGroup): string {
  return group.source.kind === "library" ? `library:${group.source.projectId}` : group.source.kind;
}
